import { axiosInstance, API_PREFIX, type ApiResponse, type RequestParams } from '@/shared/api';
import type { ApiResponseList } from '@/shared/api/types';
import type {
  PartnerBase,
  PartnerById,
  PartnerCreatePayload,
  PartnerListItem,
  PartnerUpdatePayload,
  PartnerResetPasswordPayload,
  PartnerItem,
  PartnerItemListParams,
  PartnerInnInfo,
  PartnerValidateInnPayload,
  PartnerProductItem,
  PartnerProductPayload,
  PartnerProductIndexItem,
  PartnerProductIndexParams,
  PartnerProductPricePayload,
} from '../model/types';

const BASE = `${API_PREFIX.ADMIN_V1}/partner`;
const PRODUCT_BASE = `${API_PREFIX.ADMIN_V1}/product`;
const PARTNER_PRODUCT_BASE = `${API_PREFIX.ADMIN_V1}/partner-product`;
const VALIDATE_INN_URL = `${BASE}/validate-inn`;

export const validateInn = async (payload: PartnerValidateInnPayload): Promise<ApiResponse<PartnerInnInfo>> => {
  const { data } = await axiosInstance.post<ApiResponse<PartnerInnInfo>>(VALIDATE_INN_URL, payload);
  return data;
};

export const getItemList = async (params?: PartnerItemListParams): Promise<ApiResponse<PartnerItem[]>> => {
  const { data } = await axiosInstance.get<ApiResponse<PartnerItem[]>>(`${BASE}/item-list`, { params });
  return data;
};

export const getList = async (params?: RequestParams): Promise<ApiResponseList<PartnerListItem>> => {
  const { data } = await axiosInstance.get<ApiResponseList<PartnerListItem>>(`${BASE}/index`, { params });
  return data;
};

export const getById = async (id: number): Promise<ApiResponse<PartnerById>> => {
  const { data } = await axiosInstance.get<ApiResponse<PartnerById>>(`${BASE}/show`, { params: { id } });
  return data;
};

export const create = async (payload: PartnerCreatePayload): Promise<ApiResponse<null>> => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(`${BASE}/create`, payload);
  return data;
};

export const update = async (payload: PartnerUpdatePayload): Promise<ApiResponse<PartnerBase>> => {
  const { data } = await axiosInstance.post<ApiResponse<PartnerBase>>(`${BASE}/update`, payload);
  return data;
};

export const remove = async (id: number): Promise<ApiResponse<null>> => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(`${BASE}/delete`, { id });
  return data;
};

export const resetPassword = async (payload: PartnerResetPasswordPayload): Promise<ApiResponse<null>> => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(`${BASE}/reset-password`, payload);
  return data;
};

export const getProductList = async (partnerId: number): Promise<ApiResponse<PartnerProductItem[]>> => {
  const { data } = await axiosInstance.get<ApiResponse<PartnerProductItem[]>>(`${PRODUCT_BASE}/item-list-partners`, {
    params: { partner_id: partnerId },
  });
  return data;
};

export const createProducts = async (payload: PartnerProductPayload): Promise<ApiResponse<null>> => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(`${PARTNER_PRODUCT_BASE}/create`, payload);
  return data;
};

export const updateProducts = async (payload: PartnerProductPayload): Promise<ApiResponse<null>> => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(`${PARTNER_PRODUCT_BASE}/update`, payload);
  return data;
};

export const getProductIndex = async (
  params?: PartnerProductIndexParams
): Promise<ApiResponseList<PartnerProductIndexItem>> => {
  const { data } = await axiosInstance.get<ApiResponseList<PartnerProductIndexItem>>(`${PARTNER_PRODUCT_BASE}/index`, {
    params,
  });
  return data;
};

export const updateProductPrice = async (payload: PartnerProductPricePayload): Promise<ApiResponse<null>> => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(`${PARTNER_PRODUCT_BASE}/update`, payload);
  return data;
};
